/* eslint-disable max-lines-per-function */
import { splitEvery } from 'ramda';
import { removeFileApiCall } from 'reactApp/api/FileRemoveApiCall';
import { removeFileWeblinksApiCall } from 'reactApp/api/weblinks/WeblinksFileRemoveApiCall';
import { IS_DOCUMENTS_DOMAIN } from 'reactApp/appHelpers/configHelpers';
import { hideAllDocsMobileFeatures } from 'reactApp/appHelpers/featuresHelpers/features/showAllDocumentsOnMobile';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { handleSendAllDocumentsXray } from 'reactApp/modules/allDocuments/allDocuments.saga';
import { AllDocumentsXrayTypes } from 'reactApp/modules/allDocuments/allDocuments.types';
import { isFolder } from 'reactApp/modules/file/utils';
import { changeHomeHistory, loadHomeFolderRequest } from 'reactApp/modules/home/home.actions';
import { modifyingStart, modifyingStop, removeFileEnded, removeFileSuccess } from 'reactApp/modules/modifying/modifying.actions';
import { removeErrors } from 'reactApp/modules/modifying/modifying.constants';
import { getIdParams, getSnackbarText } from 'reactApp/modules/modifying/modifying.helpers';
import { type PublishItem, EModifyReason, ESnackbarType } from 'reactApp/modules/modifying/modifying.types';
import { closePopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import { historyPush } from 'reactApp/modules/router/router.module';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { resetSelect } from 'reactApp/modules/selections/selections.actions';
import { SelectionsSelectors } from 'reactApp/modules/selections/selections.selectors';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { isMountedFolder } from 'reactApp/modules/storage/folder.helpers';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { getCurrentFolder as getCurrentFolderAction } from 'reactApp/modules/storage/storage.selectors';
import { type CloudItem, EStorageType } from 'reactApp/modules/storage/storage.types';
import type { IChangeItem } from 'reactApp/modules/uploadList/uploadList.model';
import { removeUploadFilesAction } from 'reactApp/modules/uploadList/uploadList.module';
import { isUploaderVisible } from 'reactApp/modules/uploadList/uploadList.selectors';
import { updateUser } from 'reactApp/modules/user/user.thunkActions';
import { store } from 'reactApp/store';
import { getGaFolderType } from 'reactApp/utils/getGaFolderType';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { captureException } from 'reactApp/utils/tracer';
import { all, delay, put, select } from 'redux-saga/effects';

import { handleUnPublish } from './publish.saga';

const removeApiCall = (idParam) => removeFileApiCall(idParam);
const removeWeblinkApiCall = (idParam) => removeFileWeblinksApiCall(idParam);

export async function removeRequest(storage, item) {
    const { isPublic } = getStorage(storage);
    const idParam = getIdParams(storage, item.id);

    try {
        const {
            data: { body: id },
        } = await (isPublic ? removeWeblinkApiCall(idParam) : removeApiCall(idParam));

        return { id };
    } catch (error: any) {
        return { error: error?.response?.body };
    }
}

export function* handleRemoveItemsRequest(items: CloudItem[]) {
    const storage = yield select(getCurrentStorage);

    const folderFolder = yield select(getCurrentFolderAction);
    const folderFolderId = folderFolder?.id && folderFolder?.id !== ROOT_FOLDER_ID ? folderFolder.id : null;

    const selectedIds = yield select(SelectionsSelectors.getSelectedIdxs);

    const isUploader = yield select(isUploaderVisible);

    const { isInlineIntegration } = getStorage(storage);

    const hideButtonText = isInlineIntegration || hideAllDocsMobileFeatures;

    yield put(modifyingStart());

    if (storage !== EStorageType.public) {
        const published = items.filter((item) => Boolean('weblink' in item && item?.weblink)) as PublishItem[];

        if (published.length) {
            yield handleUnPublish({ items: published });
        }
    }

    const deletedItems: CloudItem[] = [];
    const itemsInfo: IChangeItem[] = [];
    const resObj = { foldersCount: 0, filesCount: 0, error: '', routeIdAfterDelete: '' };
    const BATCH_SIZE = 100;
    const chunks = splitEvery<CloudItem>(BATCH_SIZE, items);
    let files = chunks.shift();
    while (files?.length) {
        const removed = yield all(files.map((item) => removeRequest(storage, item)));

        files.reduce((count, item, index) => {
            const file = removed[index];

            if (file.error) {
                if (!count.error) {
                    count.error = file.error;
                }

                return count;
            }

            const isFile = !isFolder(item);
            if (!isFile && !count.routeIdAfterDelete && folderFolderId && folderFolderId === item.id) {
                count.routeIdAfterDelete = folderFolder.parent || ROOT_FOLDER_ID;
            }
            count.filesCount += Number(isFile);
            count.foldersCount += Number(!isFile);
            deletedItems.push(item);
            itemsInfo.push({ isFolder: !isFile, id: item.id });

            return count;
        }, resObj);

        files = chunks.shift();

        yield delay(1);
    }

    if (resObj.error) {
        yield put(
            showSnackbarAction({
                id: 'remove-items-error',
                type: SnackbarTypes.failure,
                text: removeErrors.getMessage(resObj.error),
                closable: true,
            })
        );

        yield handleSendAllDocumentsXray({ action: 'remove_file', xrayType: AllDocumentsXrayTypes.onError });

        captureException(resObj.error, { message: 'REMOVE' });
    }

    if (!deletedItems.length) {
        yield put(modifyingStop({ storage, reason: EModifyReason.remove }));
        yield closePopupHelper(popupNames.REMOVE_DIALOG);

        return;
    }

    yield put(
        showSnackbarAction({
            closable: true,
            id: 'delete-items-success',
            type: SnackbarTypes.trashbin,
            text: getSnackbarText({
                items: deletedItems,
                foldersCount: resObj.foldersCount,
                filesCount: resObj.filesCount,
                type: ESnackbarType.MOVE_TO_TRASHBIN,
            }),
            buttonText: !hideButtonText ? 'Перейти в корзину' : undefined,
            onButtonClick: () => {
                const id =
                    storage === EStorageType.alldocuments && !IS_DOCUMENTS_DOMAIN
                        ? `/${EStorageType.alldocuments}/${EStorageType.trashbin}`
                        : EStorageType.trashbin;

                store.dispatch(historyPush({ id }));
            },
        })
    );

    yield handleSendAllDocumentsXray({ action: 'remove_file', xrayType: AllDocumentsXrayTypes.onSuccess });

    deletedItems.forEach((deletedItem) => {
        if (isFolder(deletedItem)) {
            sendPaymentGa({
                eventCategory: ECategoryGa.folder,
                action: 'delete-folder',
                type_folder: getGaFolderType(folderFolder),
            });
        }
    });

    const deletedIds = deletedItems.map((item) => item.id);
    yield put(removeFileSuccess({ ids: deletedIds }));
    yield put(removeUploadFilesAction(itemsInfo));

    // @ts-ignore
    yield put(updateUser());

    if (isMountedFolder(folderFolder)) {
        yield put(loadHomeFolderRequest({ id: folderFolder.home, isFolder: true, force: true }));
    }

    if (deletedIds.length === selectedIds.length) {
        yield put(resetSelect());
    }

    yield put(modifyingStop({ storage, reason: EModifyReason.remove }));
    if (isUploader) {
        // Чтобы обновлять папку в аплоадере https://jira.vk.team/browse/CLOUDWEB-14284
        // И не обновлять без него https://jira.vk.team/browse/CLOUDWEB-16945
        yield put(removeFileEnded({ ids: deletedIds }));
    }
    yield closePopupHelper(popupNames.REMOVE_DIALOG);

    if (resObj.routeIdAfterDelete) {
        yield put(changeHomeHistory({ id: resObj.routeIdAfterDelete }));
    }
}
